/**
 * EventBridge — translates a stream of normalized backend events into calls on a
 * visualization target, using a declarative animation registry.
 *
 * It owns the scene lifecycle (reset + setScene on connect) and the per-event
 * dispatch: drop events for other topologies, look up the handler for the event's
 * category, and run it in isolation so one bad event never breaks the live stream.
 */

import { ANIMATION_REGISTRY } from "./animationRegistry.js";
import type {
  AnimationRegistry,
  BridgeContext,
  EventSource,
  NormalizedEvent,
  VisualizationTarget,
} from "./types.js";

export class EventBridge {
  private target: VisualizationTarget | null = null;
  private ctx: BridgeContext | null = null;

  constructor(
    private readonly source: EventSource,
    private readonly registry: AnimationRegistry = ANIMATION_REGISTRY,
  ) {}

  /** Bind a target + scene context and start consuming events. */
  connect(target: VisualizationTarget, ctx: BridgeContext): void {
    this.target = target;
    this.ctx = ctx;
    target.reset();
    target.setScene(ctx.topologyId, ctx.topologyType);
    this.source.start((event) => this.dispatch(event));
  }

  /** Stop consuming events. */
  disconnect(): void {
    this.source.stop();
    this.target = null;
    this.ctx = null;
  }

  private dispatch(event: NormalizedEvent): void {
    const target = this.target;
    const ctx = this.ctx;
    if (!target || !ctx) return;

    // Topology filter — event frames are not filtered server-side, so scope here.
    // Events without a topologyId (world-level) are always processed.
    if (event.topologyId && event.topologyId !== ctx.topologyId) return;

    const handler = this.registry[event.category];
    if (!handler) return;

    try {
      handler(event, target, ctx);
    } catch {
      // A single malformed event must never break the live stream.
    }
  }
}
